<template>
  <div class="container-fluid full-height-page-wrapper">
    <TableRowModal v-model="mixin_tableRowModal.show" :title="mixin_tableRowModal.title"
      :items="mixin_tableRowModal.items" :tableRowModalFields="mixin_tableRowModal.fields" />
    <div class="row full-height-content">
      <div class="col-12 full-height-content">
        <div>

          <PageHeader showBreadcrumbs page-title="Finance reports - profit and loss detailed">
            <template #buttons>
              <b-button class="button mr-2" :variant="isShowBulkDownload ? 'secondary' : 'info'"
                @click="toggleShowBulkDownload">
                <span class="material-icons icon-image-preview">
                  tune
                </span>
                Bulk download invoices
              </b-button>
              <b-button class="button" :variant="isShowFilter ? 'secondary' : 'info'" @click="toggleShowFilter">
                <span class="material-icons icon-image-preview">
                  tune
                </span>
                Filters
              </b-button>
            </template>
          </PageHeader>

          <h5 v-if="isShowBulkDownload">Bulk download invoices (invoices, payouts, expenses files)</h5>
          <BulkDownloadInvoices :show="isShowBulkDownload" @update:show="show => isShowBulkDownload = show" />
          <Filters v-show="isShowFilter" textButtonSubmit="Get Report" :data="mixin_filters" withGroups resetToInitial
            :isLoading="isDataPageLoading" @submit="_getReportsProfitAndLoss" />

          <div v-if="isDataPageLoading && isShowFilter" class="content-loading-wrapper">
            <Preloader class="centered-preloader" />
          </div>

          <TableForPage ref="table" v-if="totalReports.length" :tableData="totalReports" :fields="fieldsReports"
            :tableDescription="columnDescription" :cell-fixed="true" showTopControls showTotalTable>
            <template v-slot:tableControlsRight>
              <b-button class="ml-auto" size="sm" @click="showColumns = !showColumns">
                <span v-if="showColumns">Hide</span>
                <span v-else>Show</span>
                RUB/GBP columns
              </b-button>
            </template>
            <template v-slot:row="slotProps">
              <template v-if="slotProps.row.item.column_group === config.groupName.expenses">
                <NestedTable style="margin-bottom: 0;" :items="slotProps.row.item.categories"
                  :fields="fieldsReportsExpenses" :modalFields="fieldsNestedExpenses" rowName="expenses"
                  :modalSlots="slotsExpenses">
                  <template v-slot:modal-comment="slotProps">
                    <div class="d-flex justify-content-center cursor-pointer" v-if="slotProps.item.comment"
                      v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title=slotProps.item.comment>
                      <font-awesome-icon icon="comment" />
                    </div>
                  </template>
                  <template v-slot:modal-status.name="slotProps">
                    <LabelTableStatus :status="slotProps.item.status" />
                  </template>
                </NestedTable>
              </template>
              <template v-if="slotProps.row.item.column_group === config.groupName.payouts">
                <NestedTable style="margin-bottom:0;" class="mb-0" :items="slotProps.row.item.payees"
                  :fields="fieldsReportsPayoutsPayees" :modalFields="fieldsNestedPayouts" rowName="payouts">
                </NestedTable>
              </template>
              <template v-if="slotProps.row.item.column_group === config.groupName.payments">
                <b-tabs card>
                  <b-tab
                    v-for="(label, fieldName) in {'business_developers': 'By business developers', 'salesChannels': 'By sales channels'}"
                    :key="fieldName" :title="label">
                    <NestedTable style="margin-bottom: 0;" :items="slotProps.row.item[fieldName]"
                      :fields="fieldsReportsPayments" :modalFields="fieldsNestedPayments" rowName="projects">
                      <template v-slot:modal-row="slotProps">
                        <NestedTable :items="slotProps.row.item.invoices" :fields="fieldsNestedPaymentsInvoices"
                          :slots="slotsPayments">
                          <template v-slot:status.name="slotProps">
                            <LabelTableStatus :status="slotProps.item.status" />
                          </template>
                          <template v-slot:type="slotProps">
                            <div class="d-flex justify-content-center cursor-pointer"
                              v-b-tooltip.hover="{ variant: 'base', placement: 'top', placement:'top', boundary:'viewport' }" :title=slotProps.item.type>
                              <font-awesome-icon icon="clock" />
                            </div>
                          </template>
                          <template v-slot:number="slotProps">
                            <LabelDocDownloadLink :file_name="slotProps.item.file_name"
                              :url="slotProps.item.preview_url" />
                          </template>
                        </NestedTable>
                      </template>
                    </NestedTable>
                  </b-tab>
                </b-tabs>
              </template>
            </template>
            <template v-slot:total>
              <b-table class="table__total table_visible" thead-class="d-none" :items="itemsReportsSummary"
                :fields="fieldsReports" :isShowMoreButton="false" :fixed="true">
                <template v-slot:total.equivalent.percent="slotProps">
                  <div
                    :class="['finance-summary-percent-' + _getClassByPercent(slotProps.item.total.equivalent.percent)]">
                    {{slotProps.item.total.equivalent.percent}} %
                  </div>
                </template>
              </b-table>
            </template>
          </TableForPage>
          `
        </div>

        <!-- Empty state when no data -->
        <div v-if="!isDataPageLoading && !totalReports.length" class="full-height-content">
          <EmptyStateNoResults
            title="No profit and loss report yet"
            description="No data to display. Configure the report filters and click Get report to see profit & loss entries here."
            :customSvg="profitLossReportSvg"
            :withoutButton="true"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { permissions } from '@mocks/permissions'
import TableRowModal from '@tables/TableRowModal'
import Filters from '@components/Filter/Filter'
import TableForPage from '@tables/TableForPage'
import NestedTable from '@tables/NestedTable'
import LabelTableStatus from '@components/tables/elements/LabelTableStatus'
import LabelDocDownloadLink from '@components/tables/elements/LabelDocDownloadLink'
import BulkDownloadInvoices from '@components/BulkDownloadInvoices'
import PageHeader from '@components/PageHeader.vue'
import Preloader from '@components/Preloader'
import EmptyStateNoResults from '@components/EmptyStateNoResults.vue'
import { profitLossReportSvg } from '@/assets/icons/profitLossReportIcon'
import FinanceReportsProfitAndLoss from '../mixins/FinanceReportsProfitAndLoss'

export default {
  name: 'FinanceReportsProfitAndLoss',
  components: {
    TableRowModal,
    Filters,
    TableForPage,
    NestedTable,
    LabelTableStatus,
    LabelDocDownloadLink,
    BulkDownloadInvoices,
    PageHeader,
    Preloader,
    EmptyStateNoResults
  },
  mixins: [FinanceReportsProfitAndLoss],
  data () {
    return {
      permissions: permissions,
      profitLossReportSvg,
      isShowFilter: true,
      isShowBulkDownload: false,
      showColumns: false,
      config: {
        groupName: {
          expenses: 'Expenses',
          payouts: 'Payouts',
          payments: 'Payments',
          summary: 'Total'
        }
      },
      currencyFields: [
        {
          key: 'total.original.UAH',
          label: 'UAH',
          sortable: true,
          class: 'column-amount'
        },
        {
          key: 'total.original.USD',
          label: 'USD',
          sortable: true,
          class: 'column-amount'
        },
        {
          key: 'total.original.EUR',
          label: 'EUR',
          sortable: true,
          class: 'column-amount'
        },
        {
          key: 'total.original.RUB',
          label: 'RUB',
          sortable: true,
          class: 'column-amount'
        },
        {
          key: 'total.original.GBP',
          label: 'GBP',
          sortable: true,
          class: 'column-amount'
        },
        {
          key: 'total.equivalent.USD',
          label: 'Eq USD',
          sortable: true,
          class: 'column-amount'
        },
        {
          key: 'total.equivalent.percent',
          label: 'Percent',
          sortable: true,
          class: 'column-amount',
          formatter: value => {
            return `${value} %`
          }
        }
      ],
      slotsPayments: {
        number: {
          name: 'number'
        },
        'status.name': {
          name: 'status.name'
        },
        'type': {
          name: 'type'
        }
      },
      slotsSummary: {
        'total.equivalent.percent': {
          name: 'total.equivalent.percent'
        }
      },
      slotsExpenses: {
        comment: {
          name: 'comment'
        },
        'status.name': {
          name: 'status.name'
        }
      },
      totalReports: [],
      itemsReportsSummary: [],
      columnDescription: [
        '<strong>Status</strong> = ',
        '<strong>Info</strong> = ',
        '<strong>Am</strong> = ',
        '<strong>Eq</strong> = '
      ]
    }
  },
  computed: {
    getCurrencyFields () {
      if (this.showColumns) {
        return this.currencyFields
      } else {
        return this.currencyFields.filter(f => {
          return f.key !== 'total.original.GBP' && f.key !== 'total.original.RUB'
        })
      }
    },
    fieldsReports () {
      return [
        {
          key: 'column_group',
          label: 'Group',
          sortable: true,
          class: 'column-bd-name font-weight-bold'
        },
        ...this.getCurrencyFields,
        {
          key: 'more',
          label: 'More',
          class: 'column-more'
        }
      ]
    },
    fieldsReportsExpenses () {
      return [
        {
          key: 'name',
          label: 'Group',
          sortable: true,
          class: 'column-bd-name'
        },
        ...this.getCurrencyFields,
        {
          key: 'more',
          label: 'More',
          class: 'column-more'
        }
      ]
    },
    fieldsReportsPayoutsPayees () {
      return [
        {
          key: 'name',
          label: 'Payee',
          sortable: true,
          class: 'column-bd-name'
        },
        ...this.getCurrencyFields,
        {
          key: 'more',
          label: 'More',
          class: 'column-more'
        }
      ]
    },
    fieldsReportsPayments () {
      return [
        {
          key: 'name',
          label: 'Name',
          sortable: true,
          class: 'column-bd-name'
        },
        ...this.getCurrencyFields,
        {
          key: 'more',
          label: 'More',
          class: 'column-more'
        }
      ]
    },
    fieldsNestedPayments () {
      return [
        {
          key: 'name',
          label: 'Name',
          sortable: true,
          class: 'column-long-name'
        },
        ...this.getCurrencyFields,
        {
          key: 'more',
          label: 'More',
          class: 'column-more'
        }
      ]
    },
    fieldsNestedPaymentsInvoices () {
      return [
        {
          key: 'hours',
          label: 'Hrs',
          sortable: true,
          class: 'column-hours'
        },
        {
          key: 'status.name',
          label: 'Status',
          sortable: true,
          class: 'column-icon text-center'
        },
        {
          key: 'type',
          label: 'Type',
          sortable: true,
          class: 'column-icon text-center'
        },
        {
          key: 'due_date',
          label: 'Due Date',
          sortable: true,
          class: 'column-type'
        },
        {
          key: 'paid_date',
          label: 'Paid Date',
          sortable: true,
          class: 'column-type'
        },
        ...this.getCurrencyFields,
        {
          key: 'number',
          label: 'Invoice',
          sortable: false,
          class: 'column-date text-center'
        }
      ]
    },
    fieldsNestedExpenses () {
      return [
        {
          key: 'comment',
          label: 'Comment',
          sortable: false,
          class: 'column-amount text-center'
        },
        ...this.getCurrencyFields,
        {
          key: 'status.name',
          label: 'Status',
          sortable: true,
          class: 'column-amount text-center'
        },
        {
          key: 'due_date',
          label: 'Due Date',
          sortable: true,
          class: 'column-amount'
        },
        {
          key: 'paid_date',
          label: 'Paid Date',
          sortable: true,
          class: 'column-amount'
        }
      ]
    },
    fieldsNestedPayouts () {
      return [
        {
          key: 'payout_number',
          label: 'Number',
          sortable: true,
          class: 'column-bd-name'
        },
        {
          key: 'amount',
          label: 'Amount',
          sortable: true,
          class: 'column-amount'
        },
        {
          key: 'currency',
          label: 'Currency',
          sortable: true,
          class: 'column-date'
        },
        {
          key: 'issue_date',
          label: 'Issue Date',
          sortable: true,
          class: 'column-date'
        },
        {
          key: 'paid_date',
          label: 'Paid Date',
          sortable: true,
          class: 'column-date'
        },
        {
          key: 'empty',
          label: '',
          class: 'column-more'
        }
      ]
    }
  },
  watch: {
    showColumns (v) {

    }
  },
  created () {
    this._fetchAllData()
  },
  methods: {
    toggleShowFilter () {
      this.isShowFilter = !this.isShowFilter
    },
    toggleShowBulkDownload () {
      this.isShowBulkDownload = !this.isShowBulkDownload
    },
    setReportsDataTotalFromRequest (data) {
      const { payoutsTotal, invoicesTotal, expensesTotal, summary } = data
      this._setDataTotalReports({ payoutsTotal, invoicesTotal, expensesTotal, summary })
    }
  }
}

</script>
